/**
 * useAutoGrowTextarea — grow a <textarea> to fit its content, capped at a max.
 *
 * The composer starts at a single line and expands line-by-line as the writer
 * types (or pastes) longer prompts, then stops at `maxHeightPx` and scrolls
 * internally. There is no autosize library in the repo — this is the small,
 * scrollHeight-based primitive that the four other raw textareas can adopt
 * later.
 *
 * # DECISION: keyed on `value` via useLayoutEffect. Every path that mutates the
 * text — typing, clearing on submit, `prefillInput`, `setReplyTarget` — flows
 * through the controlled `value`, so keying the resize on it covers all of them
 * for free (including collapsing back to one line when the field is cleared).
 * useLayoutEffect corrects the height before paint so the field never flashes
 * at the wrong size.
 */

import { useCallback, useLayoutEffect, useRef } from "react";

export function useAutoGrowTextarea(value: string, maxHeightPx: number) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Reset to "auto" first so scrollHeight reflects the *content* height and
    // the field can shrink as well as grow (without the reset, scrollHeight
    // never drops below the current fixed height).
    el.style.height = "auto";
    const next = Math.min(el.scrollHeight, maxHeightPx);
    el.style.height = `${next}px`;
    // Only show the scrollbar once we've hit the cap — below it the field is
    // exactly content-height, so a scrollbar would be dead chrome.
    el.style.overflowY = el.scrollHeight > maxHeightPx ? "auto" : "hidden";
  }, [maxHeightPx]);

  // `value` is the reactive trigger — resize() measures the DOM, not `value`,
  // but the field must be re-measured on every text change. resize is stable
  // across value changes (depends only on maxHeightPx), so it alone would
  // never re-fire.
  // biome-ignore lint/correctness/useExhaustiveDependencies: value is an intentional re-measure trigger, not read inside the effect
  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  return { ref, resize };
}

export default useAutoGrowTextarea;
